/**
 * The room: three areas in one SVG, a camera over it, and people who can be moved.
 *
 * Built from the Bug Hunt's own parts — the same figures, desks, cards, mosquitoes and jar — so a
 * person looks the same here as there. What is new is the camera (zoom and pan over a room three
 * floors wide) and dragging: a viewer can move someone within their area, and the spot is kept.
 *
 * Pointer handling in one place, because three gestures share the same surface:
 *   - press on a person and move: drag them (a click without movement still selects);
 *   - press on the floor and move: pan the camera;
 *   - wheel: zoom around the pointer.
 */

import { memo, useEffect, useMemo, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'
import type { BugEntity, DeveloperEntity } from '../bughunt/store'
import { AllClear, BugGroup, Containment, SceneDefs, StationGroup, type SceneLabels } from '../bughunt/Scene'
import { JAR_CELLS, assignJarCells, containmentBox, isCrowded, seedFrom, type FloorSize } from '../bughunt/layout'
import { useOnScreen } from '../bughunt/useOnScreen'
import { isAtStation, isContained, urgency, visualStateFor, type VisualBugState } from '../bughunt/visual'
import { AREAS, ROOM_WIDTH, type AreaId, type Placed } from './areas'
import { AreaBackdrop, AreaDividers } from './Decor'
import { useScenery } from '../bughunt/scenery'
import type { BugHuntSession, SpecSummary } from '../lib/types'

export interface RoomSceneProps {
  svgRef: RefObject<SVGSVGElement>
  size: FloorSize
  viewBox: string
  labels: SceneLabels
  developers: DeveloperEntity[]
  placed: Map<string, Placed>
  bugs: BugEntity[]
  boards: SpecSummary[]
  areaTitles: Record<AreaId, string>
  areaPeople: Record<AreaId, string>
  selectedDeveloperId: string | null
  selectedBugId: string | null
  marked: Set<string>
  /** Prompt counts to show beside each card, when the viewer may see them. */
  prompts: Map<string, number>
  /** What each person's agent is on, in a few words. */
  details: Map<string, string | null>
  /**
   * The project each person's main agent is on, by developer id — given when no project is
   * filtered, so the room says who is on which. Absent: no labels.
   */
  projects?: Map<string, string | null>
  /** The project of any one session, for the clones of a person running several agents. */
  projectOf?: (session: BugHuntSession) => string | null
  /** The session each person's card describes, by developer id; the other busy ones are clones. */
  primaries?: Map<string, string>
  /** What each speaking character is saying right now, by developer id. */
  speech?: Map<string, string>
  /** How much larger speech bubbles are drawn, so they stay readable zoomed out. */
  speechScale?: number
  reducedMotion: boolean
  onSelectDeveloper: (id: string | null) => void
  onSelectBug: (id: string | null) => void
  onOpenSpecialist: (developerId: string) => void
  onOpenSpec: (id: string) => void
  onOpenJar: () => void
  onDrag: (developerId: string, area: AreaId, x: number, y: number, done: boolean) => void
  onPan: (dx: number, dy: number) => void
  onZoom: (factor: number, around: { x: number; y: number }) => void
}

/** How far a press has to move before it is a drag rather than a click, in screen pixels. */
const DRAG_THRESHOLD = 4

type Gesture =
  | { kind: 'station'; id: string; area: AreaId; startX: number; startY: number; dx: number; dy: number; dragging: boolean }
  | { kind: 'pan'; lastX: number; lastY: number; moved: boolean }

export const RoomScene = memo(function RoomScene(props: RoomSceneProps) {
  const {
    svgRef, size, viewBox, labels, developers, placed, bugs, boards, areaTitles, areaPeople,
    selectedDeveloperId, selectedBugId, marked, prompts, details, reducedMotion, speech, speechScale, projects, projectOf, primaries,
    onSelectDeveloper, onSelectBug, onOpenSpecialist, onOpenSpec, onOpenJar, onDrag, onPan, onZoom,
  } = props

  const gesture = useRef<Gesture | null>(null)
  // Off screen or in a hidden tab, the animations pause; with many agents, lines only for the selection (phase 4).
  const onScreen = useOnScreen(svgRef)
  const crowded = useMemo(() => isCrowded(developers), [developers])
  const swallowClick = useRef(false)

  /** A point on screen, in room units. */
  const toRoom = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const svg = svgRef.current
    const matrix = svg?.getScreenCTM()
    if (!svg || !matrix) return null
    const point = svg.createSVGPoint()
    point.x = clientX
    point.y = clientY
    const local = point.matrixTransform(matrix.inverse())
    return { x: local.x, y: local.y }
  }

  // The wheel needs a non-passive listener to stop the page from scrolling while zooming.
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const around = toRoom(event.clientX, event.clientY)
      if (around) onZoom(event.deltaY < 0 ? 1.12 : 1 / 1.12, around)
    }
    svg.addEventListener('wheel', onWheel, { passive: false })
    return () => svg.removeEventListener('wheel', onWheel)
    // toRoom reads the ref at call time; the listener only needs to change with onZoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgRef, onZoom])

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return

    // A new press starts clean. The click that ends a drag is not always delivered, and a flag left
    // over from one would swallow the next real click.
    swallowClick.current = false
    const station = (event.target as Element).closest('[data-station]')

    if (station) {
      const id = station.getAttribute('data-station')!
      const area = station.getAttribute('data-area') as AreaId
      const spot = placed.get(id)
      const start = toRoom(event.clientX, event.clientY)
      if (!spot || !start) return
      gesture.current = { kind: 'station', id, area, startX: event.clientX, startY: event.clientY, dx: start.x - spot.x, dy: start.y - spot.y, dragging: false }
    } else {
      gesture.current = { kind: 'pan', lastX: event.clientX, lastY: event.clientY, moved: false }
    }

    // No pointer capture yet. Capturing here would retarget the click that follows a plain press
    // to the room itself, so clicking a person, a bug or a board would deselect instead of select.
    // It is taken below, once the press has become a drag or a pan.
  }

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const current = gesture.current
    if (!current) return

    if (current.kind === 'station') {
      if (!current.dragging && Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < DRAG_THRESHOLD) return
      if (!current.dragging) svgRef.current?.setPointerCapture(event.pointerId)
      current.dragging = true
      const at = toRoom(event.clientX, event.clientY)
      if (at) onDrag(current.id, current.area, at.x - current.dx, at.y - current.dy, false)
      return
    }

    const matrix = svgRef.current?.getScreenCTM()
    if (!matrix) return
    const dx = (event.clientX - current.lastX) / matrix.a
    const dy = (event.clientY - current.lastY) / matrix.d
    if (!current.moved && Math.abs(event.clientX - current.lastX) + Math.abs(event.clientY - current.lastY) > 0) {
      current.moved = true
      svgRef.current?.setPointerCapture(event.pointerId)
    }
    current.lastX = event.clientX
    current.lastY = event.clientY
    onPan(-dx, -dy)
  }

  const onPointerUp = (event: ReactPointerEvent<SVGSVGElement>) => {
    const current = gesture.current
    gesture.current = null
    if (svgRef.current?.hasPointerCapture(event.pointerId)) svgRef.current.releasePointerCapture(event.pointerId)
    if (!current) return

    if (current.kind === 'station' && current.dragging) {
      const at = toRoom(event.clientX, event.clientY)
      if (at) onDrag(current.id, current.area, at.x - current.dx, at.y - current.dy, true)
      swallowClick.current = true
    } else if (current.kind === 'pan' && current.moved) {
      swallowClick.current = true
    }
  }

  // ------------------------------------------------------------------ bugs

  const stationById = placed
  const sortedBugs = useMemo(
    () => [...bugs].sort((a, b) => urgency(a.state, a.priority) - urgency(b.state, b.priority)),
    [bugs],
  )

  const visuals = useMemo(() => {
    const map = new Map<string, VisualBugState>()
    for (const bug of sortedBugs) {
      map.set(bug.taskId, visualStateFor(bug.state, Boolean(bug.developerId && stationById.has(bug.developerId))))
    }
    return map
  }, [sortedBugs, stationById])

  const contained = sortedBugs.filter((bug) => isContained(visuals.get(bug.taskId)!))
  const shownInJar = contained.slice(0, JAR_CELLS)
  const cells = assignJarCells(shownInJar.map((bug) => seedFrom(bug.taskId)))
  const jarCells = new Map(shownInJar.map((bug, i) => [bug.taskId, cells[i]!]))

  const slots = useMemo(() => {
    const index = new Map<string, number>()
    const perStation = new Map<string, number>()
    for (const bug of sortedBugs) {
      const visual = visuals.get(bug.taskId)!
      if (!bug.developerId || !(isAtStation(visual) || visual === 'walking')) continue
      const n = perStation.get(bug.developerId) ?? 0
      index.set(bug.taskId, n)
      perStation.set(bug.developerId, n + 1)
    }
    return { index, hunters: new Set(perStation.keys()) }
  }, [sortedBugs, visuals])

  // Nearer stations drawn over further ones: by where they stand, not by the order they arrived.
  const ordered = developers
    .filter((d) => placed.has(d.id))
    .sort((a, b) => placed.get(a.id)!.y - placed.get(b.id)!.y)

  const jar = containmentBox(size)
  const scenery = useScenery()

  return (
    <svg
      ref={svgRef}
      className={`bh-scene room-scene scenery--${scenery.time}${reducedMotion || !onScreen ? ' bh-scene--still' : ''}`}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={AREAS.map((a) => areaTitles[a]).join(', ')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={(event) => {
        if (swallowClick.current) {
          swallowClick.current = false
          event.stopPropagation()
        }
      }}
      onClick={() => {
        onSelectDeveloper(null)
        onSelectBug(null)
      }}
    >
      <SceneDefs />

      {/* Beyond the room: dark, so zooming out shows the building's edge rather than nothing. */}
      <rect x={-ROOM_WIDTH} y={-size.height * 2} width={ROOM_WIDTH * 3} height={size.height * 5} fill="#070B14" />

      {AREAS.map((area) => (
        <AreaBackdrop
          key={area}
          area={area}
          size={size}
          labels={labels}
          title={areaTitles[area]}
          people={areaPeople[area]}
          boards={area === 'features' ? boards : []}
          onOpenSpec={onOpenSpec}
        />
      ))}

      <AreaDividers size={size} />

      {ordered.map((developer) => {
        const station = placed.get(developer.id)!
        return (
          <g
            key={developer.id}
            data-station={developer.id}
            data-area={station.area}
            className="room-station"
            onDoubleClick={(event) => {
              event.stopPropagation()
              onOpenSpecialist(developer.id)
            }}
          >
            <StationGroup
              station={station}
              developer={developer}
              hunting={slots.hunters.has(developer.id)}
              selected={selectedDeveloperId === developer.id}
              labels={labels}
              onSelect={onSelectDeveloper}
              marked={marked.has(developer.id)}
              detail={details.get(developer.id) ?? null}
              showPrompts={prompts.get(developer.id)}
              speech={speech?.get(developer.id)}
              speechScale={speechScale}
              project={projects?.get(developer.id) ?? null}
              projectOf={projects ? projectOf : undefined}
              primarySessionId={primaries?.get(developer.id)}
              crowded={crowded}
            />
          </g>
        )
      })}

      {/* A teammate's agent on a part of somebody's task (docs/refactor/15 phase 2, 25 phase 3b): a line from that
          agent to the person it works for, besides the one to its own person. Real data only: the task's parts. */}
      <g className="bh-team-links">
        {ordered.flatMap((developer) => {
          const station = placed.get(developer.id)!
          const primaryId = primaries?.get(developer.id)
          const primary = (primaryId ? developer.sessions.find((s) => s.workSessionId === primaryId) : undefined) ?? developer.sessions[0]
          const busy = developer.sessions.filter((s) => s.activityState !== 'Idle')
          const clones = primaryId ? busy.filter((s) => s.workSessionId !== primary?.workSessionId) : busy.slice(1)
          return developer.sessions.flatMap((session) => {
            const owner = session.helpingDeveloperId ? placed.get(session.helpingDeveloperId) : undefined
            if (!owner || session.helpingDeveloperId === developer.id) return []
            const cloneIndex = clones.findIndex((c) => c.workSessionId === session.workSessionId)
            let from: { x: number; y: number } | null = null
            if (session.workSessionId === primary?.workSessionId) from = { x: station.x - 58 * station.scale, y: station.y - 112 * station.scale }
            else if (cloneIndex >= 0 && cloneIndex < 3) from = { x: station.x + (70 + cloneIndex * 48) * station.scale, y: station.y + (4 + (cloneIndex % 2) * 6 - 30) * station.scale }
            if (!from) return []
            const to = { x: owner.x + 12 * owner.scale, y: owner.y - 70 * owner.scale }
            const lit = selectedDeveloperId === developer.id || selectedDeveloperId === session.helpingDeveloperId
            const mid = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 120 }
            return [
              <path
                key={`team-${session.workSessionId}`}
                className={`bh-ag-link bh-ag-link--team${lit ? ' bh-ag-link--on' : ''}`}
                d={`M ${from.x} ${from.y} Q ${mid.x} ${mid.y} ${to.x} ${to.y}`}
                data-helping={`${developer.id}>${session.helpingDeveloperId}`}
              >
                <title>{`${developer.name} → ${session.helpingName ?? ''}`}</title>
              </path>,
            ]
          })
        })}
      </g>

      <Containment
        box={jar}
        count={contained.length}
        overflow={contained.length - shownInJar.length}
        label={labels.containment}
        waitingLabel={labels.waiting(contained.length)}
        moreLabel={labels.andMore(contained.length - shownInJar.length)}
        onClick={onOpenJar}
      />

      {sortedBugs.map((bug) => {
        const visual = visuals.get(bug.taskId)!
        if (isContained(visual) && !jarCells.has(bug.taskId)) return null
        const station = bug.developerId ? stationById.get(bug.developerId) : undefined

        return (
          <BugGroup
            key={bug.taskId}
            bug={bug}
            visual={visual}
            station={station}
            stationSlot={slots.index.get(bug.taskId) ?? 0}
            jarCell={jarCells.get(bug.taskId)}
            size={size}
            selected={selectedBugId === bug.taskId}
            labels={labels}
            onSelect={onSelectBug}
          />
        )
      })}

      {bugs.length === 0 && <AllClear box={jar} title={labels.allClear} hint={labels.allClearHint} />}
    </svg>
  )
})
